'use client';

import React, { useState, useEffect } from 'react';
import { motion, Variants, Transition } from 'framer-motion';

// Define the structure of a question object
interface Question {
  id: number;
  question: string;
  options: { text: string; description: string }[]; // Updated options structure
}

interface AssessmentStepProps {
  currentQuestion: number;
  questions: Question[]; // Use the updated Question interface
  dimensions: (string | string[])[]; // Allow string arrays for multi-line labels
  onAnswer: (answerIndex: number, times?: number[]) => void;
  onBack: () => void;
  pageVariants?: Variants;
  pageTransition?: Transition;
}

const AssessmentStep: React.FC<AssessmentStepProps> = ({
  currentQuestion,
  questions,
  dimensions,
  onAnswer,
  onBack,
  pageVariants,
  pageTransition,
}) => {
  const variants = pageVariants || {};
  const transition = pageTransition || {};

  // Add state to track hovered button index
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [questionTimes, setQuestionTimes] = useState<number[]>([]);
  const [lastTimestamp, setLastTimestamp] = useState<number>(() => Date.now());

  const progressPercent = (currentQuestion / questions.length) * 100;
  const currentQuesData = questions[currentQuestion];

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!sessionStorage.getItem('assessmentStartTime')) {
        sessionStorage.setItem('assessmentStartTime', Date.now().toString());
      }
    }
  }, []);

  // Handler for answering a question
  const handleAnswer = (answerIndex: number) => {
    const now = Date.now();
    const delta = Math.floor((now - lastTimestamp) / 1000); // seconds
    setQuestionTimes((prev) => {
      const updated = [...prev];
      updated[currentQuestion] = delta;
      return updated;
    });
    setLastTimestamp(now);
    onAnswer(answerIndex, [...questionTimes.slice(0, currentQuestion), delta]);
  };

  return (
    <motion.div
      key="assessment"
      initial="initial"
      animate="in"
      exit="out"
      variants={variants}
      transition={transition}
    >
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="relative mb-2">
          <div className="h-4 w-full overflow-hidden rounded-full bg-gray-200 shadow-inner">
            <motion.div
              className="h-full rounded-full bg-[#59B9C1]/75 shadow-md"
              style={{ width: `${progressPercent}%` }}
              initial={{ width: `${((currentQuestion - 1) / questions.length) * 100}%` }} // Start from previous width
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
          <motion.span
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold text-white mix-blend-difference"
            key={currentQuestion} // Animate text change
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            {Math.round(progressPercent)}%
          </motion.span>
        </div>
        <p className="text-center text-sm text-gray-400">
          Question {currentQuestion + 1} of {questions.length}
        </p>
      </div>

      {/* Question Area */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white mb-8">
          {/* Display dimension label (handle array for multi-line) */}
          {Array.isArray(dimensions[currentQuestion])
            ? (dimensions[currentQuestion] as string[]).join(' ')
            : dimensions[currentQuestion]}
        </h2>
        <p className="text-xl mb-10 text-gray-300 font-medium leading-relaxed">
          {currentQuesData.question}
        </p>
        <div className="flex flex-col gap-4 items-stretch">
          {/* Map over the options in the current question data */}
          {currentQuesData.options.map((option, index) => {
            const levelLabels: { [key: number]: string } = {
              0: 'BASIC',
              1: 'INTERMEDIATE',
              2: 'ADVANCED',
              3: 'EXPERT',
            };

            // Use Brand Red/Green transparent classes, keep Grey classes
            const bgClasses: { [key: number]: string } = {
              0: 'bg-[#F85640]/75', // Basic: Direct hex for Brand Red 75%
              1: 'bg-gray-200/75', // Intermediate: Grey 75%
              2: 'bg-gray-500/75', // Advanced: Grey 75%
              3: 'bg-[#59B9C1]/75', // Expert: Direct hex for Brand Green 75%
            };

            // Use solid Brand Red/Green hex codes for hover using the inline pseudo-class approach
            const hoverClasses: { [key: number]: string } = {
              0: '[&:hover]:bg-[#F85640]', // Inline pseudo-class for hover
              1: '[&:hover]:bg-gray-300', // Inline pseudo-class for hover
              2: '[&:hover]:bg-gray-600', // Inline pseudo-class for hover
              3: '[&:hover]:bg-[#59B9C1]', // Inline pseudo-class for hover
            };

            // Ensure correct text colors
            const textColors: { [key: number]: string } = {
              0: 'text-white', // Red bg
              1: 'text-gray-800', // Light Grey bg
              2: 'text-white', // Dark Grey bg
              3: 'text-white', // Green bg
            };

            // Base classes with transform/transition
            const baseButtonClasses =
              'block w-full rounded-lg border-none px-6 py-3 text-left font-semibold transition-all duration-200 ease-out relative'; // Use transition-all

            // Determine className: Use dynamic class for all indices, add hover class with inline pseudo
            const buttonClassName = ` ${baseButtonClasses} ${bgClasses[index]} ${textColors[index]} ${hoverClasses[index]} `;

            return (
              // Use motion.div for hover events, remove group class
              <motion.div
                key={index}
                className="relative z-10" // Keep relative and z-10, remove group
                onHoverStart={() => setHoveredIndex(index)}
                onHoverEnd={() => setHoveredIndex(null)}
              >
                <motion.button
                  onClick={() => handleAnswer(index)}
                  className={buttonClassName}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {/* Level Label */}
                  <span
                    className={`absolute left-4 top-1/2 -translate-y-1/2 text-xs font-bold uppercase tracking-wider bg-white/20 backdrop-blur-sm px-2 py-1 rounded ${index === 1 ? 'text-gray-800' : 'text-white'}`}
                  >
                    {levelLabels[index]}
                  </span>
                  {/* Option Text - Use option.text */}
                  <span className="block pl-32 text-base leading-tight">{option.text}</span>
                </motion.button>
                {/* Tooltip Span - Adjust styling */}
                <motion.span
                  className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-max max-w-sm px-4 py-2 
                            bg-gray-700 text-white text-sm rounded-md shadow-lg 
                            opacity-0 transition-opacity duration-200 ease-in-out pointer-events-none z-10" // Updated padding, max-width, background, text-size
                  animate={{ opacity: hoveredIndex === index ? 1 : 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  {option.description}
                  {/* Adjust arrow color to match new background */}
                  <motion.span
                    className="absolute left-1/2 -translate-x-1/2 top-full -mt-px w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-700" // Updated border-t-gray-700
                    animate={{ opacity: hoveredIndex === index ? 1 : 0 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                  />
                </motion.span>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8">
          <button
            onClick={onBack}
            className="rounded-lg border-none bg-gray-500/75 px-6 py-3 font-semibold text-white transition-all duration-200 ease-out [&:hover]:bg-gray-600 [&:hover]:scale-105 [&:active]:scale-95"
          >
            Go Back
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default AssessmentStep;
